import { DomainError, LOGO_MAX_BYTES, logoAttempts, logoFileError, logoOutputType, sniffLogoType } from "@szerviz/core";
import type { PreparedLogo } from "./data";

/**
 * The workshop's logo is shrunk on the device before upload (rules: packages/core/src/logo.ts): only
 * real PNG, JPEG or WebP files (checked from the bytes, so a renamed SVG is refused), at most 600 x 300 px
 * keeping the aspect ratio, PNG and WebP become PNG with their transparency, JPEG stays JPEG, at most
 * 300 KB. Throws LOGO_TYPE or LOGO_TOO_LARGE.
 */
export async function prepareLogo(file: Blob & { type: string }): Promise<PreparedLogo> {
  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const bad = logoFileError(file, head);
  if (bad) throw new DomainError(bad);
  const type = logoOutputType(sniffLogoType(head)!);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new DomainError("LOGO_TYPE");
  }
  try {
    for (const a of logoAttempts(type, bitmap.width, bitmap.height)) {
      const canvas = document.createElement("canvas");
      canvas.width = a.width;
      canvas.height = a.height;
      const ctx = canvas.getContext("2d")!;
      if (type === "image/jpeg") {
        // JPEG has no transparency: white behind it, like paper
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, a.width, a.height);
      }
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(bitmap, 0, 0, a.width, a.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, a.quality));
      if (blob && blob.size <= LOGO_MAX_BYTES) return { blob, width: a.width, height: a.height, type };
    }
  } finally {
    bitmap.close?.();
  }
  throw new DomainError("LOGO_TOO_LARGE");
}
